import { darken } from '@mui/material/styles';
import { PLATFORMS } from '@/consts/dashboardApps';
import { parseResponseError } from '@/utils/api';
import { fLocaleNumber } from '@/utils/formatNumber';
import { fDateTime, fToNow } from '@/utils/formatTime';

export const WEBHOOK_SUCCESS_WARNING_THRESHOLD = 95;
export const WEBHOOK_V2_PLATFORM_TYPE = 'webhook_v2';
export const WEBHOOK_PLATFORM_TYPES = [PLATFORMS.webhooks, WEBHOOK_V2_PLATFORM_TYPE];
export const WEBHOOK_HISTORY_PLACEHOLDER_DATA = Object.freeze({
  count: 0,
  results: [],
});

export function normalizeWebhookPlatformsResponse(data) {
  if (Array.isArray(data)) {
    return data;
  }

  return data?.results || [];
}

export function sortWebhooksByUpdatedAtDesc(items = []) {
  return [...items].sort((left, right) => {
    const leftDate = new Date(left.updatedAt || left.createdAt || 0).getTime();
    const rightDate = new Date(right.updatedAt || right.createdAt || 0).getTime();

    return rightDate - leftDate;
  });
}

export function getWebhookAppPlatforms(platforms = []) {
  return platforms.filter((platform) => WEBHOOK_PLATFORM_TYPES.includes(platform.type));
}

export function getDefaultWebhookPlatform(platforms = []) {
  return (
    platforms.find((platform) => platform.type === PLATFORMS.webhooks) ||
    platforms.find((platform) => platform.type === WEBHOOK_V2_PLATFORM_TYPE) ||
    platforms[0] ||
    null
  );
}

export function getWebhookPlatformIds(platforms = [], fallbackPlatform = null) {
  const webhookPlatform = fallbackPlatform || getDefaultWebhookPlatform(platforms);
  const webhookV1 = platforms.find((platform) => platform.type === PLATFORMS.webhooks);
  const webhookV2 = platforms.find((platform) => platform.type === WEBHOOK_V2_PLATFORM_TYPE);
  const fallbackWebhookV1Id = webhookPlatform?.type === PLATFORMS.webhooks ? webhookPlatform.id : null;
  const fallbackWebhookV2Id =
    webhookPlatform?.type === WEBHOOK_V2_PLATFORM_TYPE ? webhookPlatform.id : null;

  return {
    webhookV1Id: webhookV1?.id || fallbackWebhookV1Id,
    webhookV2Id: webhookV2?.id || fallbackWebhookV2Id,
  };
}

export function resolveWebhookPlatform(webhook = {}, platforms = [], fallbackPlatform = null) {
  const platformTypeCandidates = [
    webhook?.platformType,
    webhook?.platform_type,
    webhook?.platform?.type,
    webhook?.platform?.slug,
    webhook?.platform?.code,
  ].filter(Boolean);

  const matchedByType = platforms.find((platform) =>
    platformTypeCandidates.some((candidate) => String(platform?.type) === String(candidate))
  );

  if (matchedByType) {
    return matchedByType;
  }

  const platformIdCandidate =
    webhook?.platformId ||
    webhook?.platform_id ||
    webhook?.platform?.id ||
    (typeof webhook?.platform === 'number' || typeof webhook?.platform === 'string'
      ? webhook.platform
      : null);

  const matchedById = platforms.find(
    (platform) => platformIdCandidate && String(platform?.id) === String(platformIdCandidate)
  );

  if (matchedById) {
    return matchedById;
  }

  if (platforms.length === 1) {
    return platforms[0];
  }

  return fallbackPlatform;
}

export function resolveWebhookPlatformType(webhook = {}, platforms = [], fallbackPlatform = null) {
  return (
    resolveWebhookPlatform(webhook, platforms, fallbackPlatform)?.type ||
    webhook?.platformType ||
    webhook?.platform_type ||
    null
  );
}

export function getWebhookHistoryQueryOptions({ enabled = true } = {}) {
  return {
    enabled,
    placeholderData: WEBHOOK_HISTORY_PLACEHOLDER_DATA,
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  };
}

// O reenvio chega aqui como o erro cru do axios, cujo `message` é o "Request failed with status
// code 422" da lib. O 422 traz o código do destino em `status_code`, mas também cobre a falha antes
// de haver resposta (conexão, DNS, TLS, timeout), que sai com `status_code` 400: por isso o texto
// diz que o destino não confirmou o recebimento, e não que ele respondeu.
export function getWebhookResendErrorMessage(error) {
  const { status, data } = error?.response || {};

  if (status === 422) {
    const destinationCode = Number.isInteger(data?.status_code) ? data.status_code : null;

    return destinationCode === null
      ? 'Erro ao reenviar evento: o destino não confirmou o recebimento.'
      : `Erro ao reenviar evento: o destino não confirmou o recebimento (código ${destinationCode}).`;
  }

  return `Erro ao reenviar evento: ${parseResponseError(error).message}`;
}

export function formatWebhookInteger(value) {
  return fLocaleNumber(value);
}

export function formatWebhookDecimal(value) {
  return fLocaleNumber(value, 1);
}

export function formatWebhookLatency(value) {
  const latency = Number(value);

  if (!Number.isFinite(latency) || latency < 0) {
    return null;
  }

  if (latency < 1000) {
    return `${Math.round(latency)}ms`;
  }

  if (latency < 60000) {
    return `${formatWebhookDecimal(latency / 1000)}s`;
  }

  const minutes = Math.floor(latency / 60000);
  const seconds = Math.round((latency % 60000) / 1000);

  if (seconds === 0) {
    return `${minutes}m`;
  }

  return `${minutes}m ${seconds}s`;
}

export function resolveWebhookDispatchedAt(activity = {}) {
  return activity.dispatchedAt ?? activity.dispatched_at ?? activity.sentAt ?? null;
}

export function isWebhookWithinLast24Hours(sentAt, referenceTime = Date.now()) {
  if (!sentAt) {
    return false;
  }

  const sentAtTimestamp = new Date(sentAt).getTime();

  if (!Number.isFinite(sentAtTimestamp)) {
    return false;
  }

  const diff = referenceTime - sentAtTimestamp;
  const last24Hours = 24 * 60 * 60 * 1000;

  return diff >= 0 && diff <= last24Hours;
}

export function formatWebhookCountLabel(value, singular, plural) {
  const count = Number(value) || 0;

  return `${count} ${count === 1 ? singular : plural}`;
}

export function resolveWebhookStatus(status) {
  const normalizedStatus = String(status || '').toLowerCase();

  if (['paused', 'inactive', 'disabled'].includes(normalizedStatus)) {
    return 'paused';
  }

  if (['error', 'failed'].includes(normalizedStatus)) {
    return 'error';
  }

  return 'active';
}

export function mapWebhookMetrics(metrics) {
  const total = Number(metrics?.total) || 0;
  const succeeded = Number(metrics?.succeeded) || 0;
  const processing = (Number(metrics?.processing) || 0) + (Number(metrics?.retrying) || 0);
  const failed = Number(metrics?.failed) || 0;
  const successRate = total > 0 ? (succeeded / total) * 100 : 0;

  return [
    {
      key: 'sent',
      label: 'Enviados',
      value: formatWebhookInteger(total),
      badge: null,
      color: 'primary',
      spark: 'upStrong',
      labelColor: 'text.primary',
      valueColor: 'text.primary',
    },
    {
      key: 'delivered',
      label: 'Entregues',
      value: formatWebhookInteger(succeeded),
      badge: total > 0 ? `${formatWebhookDecimal(successRate)}%` : null,
      color: 'success',
      spark: 'upStrong',
      labelColor: 'text.primary',
      valueColor: 'success.main',
    },
    {
      key: 'processing',
      label: 'Em processamento',
      value: formatWebhookInteger(processing),
      badge: null,
      color: 'warning',
      spark: 'upDown',
      labelColor: 'text.primary',
      valueColor: 'warning.main',
    },
    {
      key: 'failed',
      label: 'Falharam',
      value: formatWebhookInteger(failed),
      badge: null,
      color: 'error',
      spark: 'downUp',
      labelColor: 'text.primary',
      valueColor: 'error.main',
    },
  ];
}

export function getWebhookMetricsUpdatedLabel(metrics = {}) {
  if (!metrics.last_event_at) {
    return 'Sem dados nas últimas 24 horas';
  }

  return `Atualizado ${fToNow(metrics.last_event_at)}`;
}

export function mapWebhook(webhook = {}) {
  const status = resolveWebhookStatus(webhook.status);
  const successRate = Number(webhook.success_rate) || 0;
  const latency = formatWebhookLatency(webhook.avg_latency);
  const deliveriesToday =
    status === 'paused' || !webhook.updatedAt ? null : `Atualizado ${fToNow(webhook.updatedAt)}`;

  return {
    id: webhook.id,
    status,
    name: webhook.name || 'Webhook sem nome',
    url: webhook.url || '-',
    deliveriesToday,
    deliveriesColor: status === 'error' ? 'error.main' : 'success.main',
    activeEvents: formatWebhookCountLabel(
      webhook.processing_events_count,
      'evento em execução',
      'eventos em execução'
    ),
    successRate: `Taxa de sucesso: ${formatWebhookDecimal(successRate)}%`,
    successRateColor:
      status === 'error' || successRate < WEBHOOK_SUCCESS_WARNING_THRESHOLD
        ? 'error.main'
        : 'text.primary',
    latency: latency ? `Latência: ${latency}` : null,
    warningMessage:
      status !== 'paused' && successRate > 0 && successRate < WEBHOOK_SUCCESS_WARNING_THRESHOLD
        ? 'Taxa de sucesso abaixo do esperado'
        : null,
    pausedInfo: status === 'paused' ? 'Pausado manualmente' : null,
    raw: webhook,
  };
}

export function getWebhookActivityType(statusCode) {
  const status = Number(statusCode);

  if (status >= 200 && status < 300) {
    return 'success';
  }

  if (!status || status >= 400) {
    return 'error';
  }

  return 'warning';
}

export function getWebhookActivityLabelColor(statusCode) {
  const type = getWebhookActivityType(statusCode);

  if (type === 'success') {
    return 'success';
  }

  if (type === 'error') {
    return 'error';
  }

  return 'warning';
}

/**
 * Texto do status "Pausado" no tema claro, o mesmo nos logs e na lista de
 * webhooks. Os tons de warning da paleta somem sobre o fundo âmbar claro do
 * selo: o `light` da lista dava 1,3:1, o `dark` do soft dos logs 3,6:1, e nem
 * o `darker` (7,3:1) ficou escuro o bastante. Um passo abaixo dele dá ~11:1.
 *
 * `darker` só existe na paleta da Cakto: num tema sem ela (o `createTheme()`
 * padrão dos testes) o `darken` recebe `undefined` e derruba a tela inteira.
 */
export function getWebhookPausedLightTextColor(theme) {
  const { warning } = theme.palette;

  return darken(warning.darker || warning.dark, 0.3);
}

export function formatWebhookActivityStatusText(activityType, statusCode, responseTime) {
  const normalizedResponseTime = Number(responseTime);
  const hasResponseTime = Number.isFinite(normalizedResponseTime);
  const responseTimeLabel = hasResponseTime ? ` (${Math.round(normalizedResponseTime)}ms)` : '';

  if (activityType === 'success') {
    return `${statusCode} OK${responseTimeLabel}`;
  }

  if (activityType === 'error') {
    return statusCode ? `${statusCode} error${responseTimeLabel}` : 'Erro ao entregar';
  }

  return statusCode ? `${statusCode}${responseTimeLabel}` : 'Em processamento';
}

export function mapWebhookActivity(activity = {}) {
  const activityType = getWebhookActivityType(activity.event_status);
  const eventKey = activity.payload?.event || activity.event_id || activity.event_name || 'evento';
  const eventLabel = activity.event_name || activity.payload?.event || activity.event_id || 'evento';
  const dispatchedAt = resolveWebhookDispatchedAt(activity);

  return {
    id: activity.id,
    type: activityType,
    timeAgo: dispatchedAt ? fToNow(dispatchedAt) : '-',
    event: eventLabel,
    eventKey,
    webhook: activity.app?.name || 'Webhook',
    statusText: formatWebhookActivityStatusText(
      activityType,
      activity.event_status,
      activity.response_time
    ),
    sentAt: dispatchedAt,
    raw: activity,
  };
}

export function mapWebhookLog(activity = {}, webhook = null) {
  const activityType = getWebhookActivityType(activity.event_status);
  const responseTime = Number(activity.response_time);
  const hasResponseTime = Number.isFinite(responseTime);
  const eventKey = activity.payload?.event || activity.event_id || activity.event_name || 'evento';
  const eventLabel = activity.event_name || activity.payload?.event || activity.event_id || 'evento';
  const dispatchedAt = resolveWebhookDispatchedAt(activity);

  return {
    id: activity.id,
    type: activityType,
    eventKey,
    event: eventLabel,
    destinationUrl: activity.url || webhook?.url || '-',
    sentAtLabel: dispatchedAt ? fDateTime(dispatchedAt, 'dd/MM/yyyy HH:mm:ss') : '-',
    timeAgo: dispatchedAt ? fToNow(dispatchedAt) : '-',
    sentAt: dispatchedAt,
    responseTime: hasResponseTime ? `${Math.round(responseTime)}ms` : '-',
    statusCode: activity.event_status,
    statusText: formatWebhookActivityStatusText(
      activityType,
      activity.event_status,
      activity.response_time
    ),
    raw: activity,
  };
}

function formatWebhookTimelineTimestamp(value) {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  const timestamp = date.getTime();

  if (!Number.isFinite(timestamp)) {
    return '-';
  }

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const milliseconds = String(date.getMilliseconds()).padStart(3, '0');

  return `${hours}:${minutes}:${seconds}.${milliseconds}`;
}

function getWebhookResponseTimeLabel(responseTime) {
  const formattedLatency = formatWebhookLatency(responseTime);

  if (formattedLatency) {
    return `Excelente (${formattedLatency})`;
  }

  return 'Nao informado';
}

function subtractMillisecondsFromDate(value, milliseconds) {
  const timestamp = new Date(value).getTime();

  if (!Number.isFinite(timestamp) || !Number.isFinite(milliseconds)) {
    return null;
  }

  return new Date(timestamp - milliseconds).toISOString();
}

const WEBHOOK_STEP_LABELS = Object.freeze({
  scheduled: 'Agendamento',
  started: 'Processamento iniciado',
  request_started: 'Enviando requisição HTTP',
  response_received: 'Resposta recebida',
  external_error: 'Erro externo na entrega',
  timeout_error: 'Timeout na entrega',
  error: 'Erro na entrega',
  finished: 'Processamento finalizado',
  retrying: 'Nova tentativa de envio',
});

export function formatWebhookStepLabel(status) {
  if (!status) {
    return 'Etapa desconhecida';
  }

  const normalized = String(status).toLowerCase();

  if (WEBHOOK_STEP_LABELS[normalized]) {
    return WEBHOOK_STEP_LABELS[normalized];
  }

  return normalized
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function buildWebhookTimelineFromSteps(steps = []) {
  if (!Array.isArray(steps) || steps.length === 0) {
    return null;
  }

  const timeline = steps
    .map((step) => {
      if (!Array.isArray(step) || step.length < 2) {
        return null;
      }

      const [time, status] = step;

      if (!time || !status) {
        return null;
      }

      return {
        time: formatWebhookTimelineTimestamp(time),
        description: formatWebhookStepLabel(status),
      };
    })
    .filter(Boolean);

  return timeline.length ? timeline : null;
}

export function buildWebhookExecutionTimeline(activity = {}) {
  const timelineFromSteps = buildWebhookTimelineFromSteps(activity.steps);

  if (timelineFromSteps) {
    return timelineFromSteps;
  }

  const responseTime = Number(activity.response_time);
  const hasResponseTime = Number.isFinite(responseTime) && responseTime >= 0;
  const responseAt =
    activity.response_received_at || activity.responded_at || activity.sentAt;
  const requestAtExplicit =
    activity.request_sent_at || activity.requested_at || activity.request_at;
  let requestAt = requestAtExplicit;

  if (!requestAt && responseAt && hasResponseTime) {
    requestAt = subtractMillisecondsFromDate(responseAt, responseTime);
  }

  if (
    requestAt &&
    responseAt &&
    new Date(requestAt).getTime() === new Date(responseAt).getTime()
  ) {
    requestAt = null;
  }

  const timelineSource = [
    {
      time: activity.generated_at || activity.createdAt || activity.created_at,
      description: 'Evento gerado pelo sistema',
    },
    {
      time: activity.queued_at || activity.enqueued_at,
      description: 'Adicionado a fila de envio',
    },
    {
      time: requestAt,
      description: 'Enviando requisição HTTP',
    },
    {
      time: responseAt,
      description: hasResponseTime
        ? `Resposta recebida (${Math.round(responseTime)}ms)`
        : 'Resposta recebida',
    },
  ];

  return timelineSource
    .filter((item) => item.time)
    .map((item) => ({
      time: formatWebhookTimelineTimestamp(item.time),
      description: item.description,
    }));
}

export function resolveWebhookExecutionIdentifier(activity = {}) {
  const payload = activity.payload || {};

  if (payload.order_id) {
    return String(payload.order_id);
  }

  if (payload.id && String(payload.id) !== String(payload.event || '')) {
    return String(payload.id);
  }

  return null;
}

export function mapWebhookExecutionDetail(activity = {}, webhook = null) {
  const eventName = activity.event_name || activity.payload?.event || activity.event_id || 'evento';
  const identifier = resolveWebhookExecutionIdentifier(activity);
  const responseTime = Number(activity.response_time);
  const hasResponseTime = Number.isFinite(responseTime) && responseTime >= 0;
  const timeline = buildWebhookExecutionTimeline(activity);
  const dispatchedAt = resolveWebhookDispatchedAt(activity);

  const requestPayload =
    activity.request?.body || activity.request_body || activity.payload || activity.request || {};
  const requestHeaders = activity.request?.headers || activity.request_headers || {};
  const responsePayload = activity.response?.body || activity.response_body || activity.response || {};
  const responseStatus =
    activity.response?.status || activity.response_status || activity.event_status || 'Nao informado';

  return {
    id: activity.id,
    eventName,
    identifier,
    sentAtLabel: dispatchedAt ? fDateTime(dispatchedAt, 'yyyy-MM-dd HH:mm:ss') : '-',
    timeline,
    requestHeaders,
    requestBody: requestPayload,
    responseStatus: String(responseStatus),
    responseTime: hasResponseTime ? `${Math.round(responseTime)}ms` : null,
    responseTimeLabel: getWebhookResponseTimeLabel(activity.response_time),
    responseBody: responsePayload,
    destinationUrl: activity.url || webhook?.url || '-',
    raw: activity,
  };
}

export function getWebhookLogsLast24HoursHistory(history = {}, referenceTime = Date.now()) {
  const results = (history?.results || []).filter((activity) =>
    isWebhookWithinLast24Hours(resolveWebhookDispatchedAt(activity), referenceTime)
  );

  return {
    ...history,
    count: results.length,
    results,
  };
}

export function mapWebhookLogMetrics({ history = {} } = {}) {
  const results = history?.results || [];
  const total = results.length;
  const successfulCount = results.filter(
    (activity) => getWebhookActivityType(activity.event_status) === 'success'
  ).length;
  const processingCount = results.filter(
    (activity) => getWebhookActivityType(activity.event_status) === 'warning'
  ).length;
  const failedCount = results.filter(
    (activity) => getWebhookActivityType(activity.event_status) === 'error'
  ).length;
  const successRate = total > 0 ? (successfulCount / total) * 100 : 0;
  const processing = processingCount;
  const delivered = successfulCount;
  const failed = failedCount;

  return [
    {
      key: 'sent',
      label: 'Enviados',
      value: formatWebhookInteger(total),
      badge: null,
      color: 'primary',
      spark: 'upStrong',
      labelColor: 'text.primary',
      valueColor: 'text.primary',
    },
    {
      key: 'delivered',
      label: 'Entregues',
      value: formatWebhookInteger(delivered),
      badge: total > 0 || successRate > 0 ? `${formatWebhookDecimal(successRate)}%` : null,
      color: 'success',
      spark: 'upStrong',
      labelColor: 'text.primary',
      valueColor: 'success.main',
    },
    {
      key: 'processing',
      label: 'Em processamento',
      value: formatWebhookInteger(processing),
      badge: null,
      color: 'warning',
      spark: 'upDown',
      labelColor: 'text.primary',
      valueColor: 'warning.main',
    },
    {
      key: 'failed',
      label: 'Falharam',
      value: formatWebhookInteger(failed),
      badge: null,
      color: 'error',
      spark: 'downUp',
      labelColor: 'text.primary',
      valueColor: 'error.main',
    },
  ];
}

export function getWebhookLogsAverageLatency({ webhook = {}, history = {} } = {}) {
  const responseTimes = (history?.results || [])
    .map((activity) => Number(activity.response_time))
    .filter((value) => Number.isFinite(value) && value >= 0);

  if (responseTimes.length > 0) {
    const average = responseTimes.reduce((sum, value) => sum + value, 0) / responseTimes.length;
    return formatWebhookLatency(average);
  }

  return '-';
}

export function getWebhookLogsUpdatedLabel(history = {}) {
  const latestSentAt = (history?.results || [])
    .map((activity) => resolveWebhookDispatchedAt(activity))
    .find(Boolean);

  if (!latestSentAt) {
    return 'Sem dados nas ultimas 24 horas';
  }

  return `Atualizado ${fToNow(latestSentAt)}`;
}

export function getWebhookLogEventOptions(logs = []) {
  const values = new Set();

  logs.forEach((log) => {
    if (log?.eventKey) {
      values.add(log.eventKey);
    }
  });

  return Array.from(values)
    .sort((left, right) => left.localeCompare(right))
    .map((value) => ({
      label: value,
      value,
    }));
}

export function filterWebhookLogs(logs = [], filter = {}) {
  const selectedStatuses = filter.status || [];
  const selectedPeriods = filter.period || [];
  const selectedEvents = filter.events || [];

  return logs.filter((log) => {
    const matchesStatus = selectedStatuses.length === 0 || selectedStatuses.includes(log.type);
    const matchesPeriod =
      selectedPeriods.length === 0 ||
      selectedPeriods.some((period) => isWebhookActivityInPeriod({ sentAt: log.sentAt }, period));
    const matchesEvent = selectedEvents.length === 0 || selectedEvents.includes(log.eventKey);

    return matchesStatus && matchesPeriod && matchesEvent;
  });
}

export function isWebhookActivityInPeriod(activity, period) {
  const effectiveSentAt = resolveWebhookDispatchedAt(activity);

  if (!effectiveSentAt || !period) {
    return true;
  }

  const sentAt = new Date(effectiveSentAt).getTime();
  const now = Date.now();
  const day = 24 * 60 * 60 * 1000;

  if (period === 'today') {
    return now - sentAt <= day;
  }

  if (period === 'yesterday') {
    return now - sentAt > day && now - sentAt <= day * 2;
  }

  if (period === 'last_7_days') {
    return now - sentAt <= day * 7;
  }

  if (period === 'last_30_days') {
    return now - sentAt <= day * 30;
  }

  if (period === 'last_60_days') {
    return now - sentAt <= day * 60;
  }

  return true;
}

export function getWebhookEventOptions(webhooks = []) {
  const values = new Set();

  webhooks.forEach((webhook) => {
    (webhook.events || []).forEach((event) => {
      const value = event.custom_id || event.name;

      if (value) {
        values.add(value);
      }
    });
  });

  return Array.from(values)
    .sort((left, right) => left.localeCompare(right))
    .map((value) => ({
      label: value,
      value,
    }));
}
